import React, { useCallback, useEffect, useState } from "react";

import { SettingGroupType } from "../../models/SettingGroupType";
import { Translations } from "../../localization/translations";

import { useTranslation } from "react-i18next";
import useClipboard from "react-use-clipboard";
import { useAppSelector } from "../../hooks/redux";
import useKlasisSnackbar from "../../utils/klasisSnackbar";
import { CustomContentProps, SnackbarContent, useSnackbar } from "notistack";

import {
    Card,
    CardContent,
    CardHeader,
    Collapse,
    IconButton,
    Typography,
    Button,
    Stack,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";

export interface ServerErrorProps {
    errorDescription: string;
    errorId: string;
}

declare module "notistack" {
    interface VariantOverrides {
        serverError: true;
    }
}

const ServerError = React.forwardRef<
    HTMLDivElement,
    ServerErrorProps & CustomContentProps
>(({ id, message, errorDescription, errorId }, ref) => {
    const { t } = useTranslation();
    const { closeSnackbar } = useSnackbar();
    const { klasisEnqueueSnackbar } = useKlasisSnackbar();

    const [isCopied, setCopied] = useClipboard(
        `${errorDescription} ${errorId}`,
        {
            successDuration: 5000, // matches the default Snackbar duration to avoid multiple copy Snackbars
        }
    );

    const systemNotifications = useAppSelector(
        (state) =>
            state.systemConfigs[SettingGroupType.frontOfficeSystemNotifications]
    );

    const [expanded, setExpanded] = useState(false);

    const handleExpand = useCallback(() => {
        setExpanded((current) => !current);
    }, []);

    useEffect(() => {
        if (isCopied) {
            klasisEnqueueSnackbar(
                t(Translations.common.label.errorCopySuccess)
            );
        }
    }, [isCopied]);

    return (
        <SnackbarContent ref={ref} role="alert">
            <Card
                sx={(theme) => ({
                    color: theme.palette.error.main,
                    backgroundColor: theme.palette.error.light,
                    minWidth: 350,
                    maxWidth: 500,
                })}
            >
                <CardHeader
                    avatar={<WarningIcon />}
                    title={
                        systemNotifications?.find(
                            (x) => x.key === "title_settingPlNotification"
                        )?.value
                    }
                    action={
                        <IconButton onClick={() => closeSnackbar(id)}>
                            <CloseIcon />
                        </IconButton>
                    }
                />
                <CardContent>
                    <Stack spacing={2}>
                        <Typography color="inherit">{message}</Typography>
                        <Stack direction="row">
                            <Button
                                sx={{
                                    color: "text.secondary",
                                }}
                                startIcon={
                                    <ExpandMoreIcon
                                        sx={{
                                            transform: expanded
                                                ? "rotate(180deg)"
                                                : "rotate(0deg)",
                                            transition: "all .2s",
                                        }}
                                    />
                                }
                                onClick={handleExpand}
                                size="small"
                            >
                                {expanded
                                    ? systemNotifications?.find(
                                          (x) =>
                                              x.key ===
                                              "title_settingPlNotificationShowLess"
                                      )?.value
                                    : systemNotifications?.find(
                                          (x) =>
                                              x.key ===
                                              "title_settingPlNotificationShowMore"
                                      )?.value}
                            </Button>
                        </Stack>
                        <Collapse
                            in={expanded}
                            sx={{
                                color: "text.primary",
                            }}
                        >
                            <Stack spacing={2}>
                                <Stack direction="row" spacing={2}>
                                    <Typography fontWeight={700}>
                                        {
                                            systemNotifications?.find(
                                                (x) =>
                                                    x.key ===
                                                    "label_settingPlNotificationDesc"
                                            )?.value
                                        }
                                        :
                                    </Typography>
                                    <Typography>{errorDescription}</Typography>
                                </Stack>
                                <Stack direction="row" spacing={2}>
                                    <Typography fontWeight={700}>
                                        {
                                            systemNotifications?.find(
                                                (x) =>
                                                    x.key ===
                                                    "label_settingPlNotificationId"
                                            )?.value
                                        }
                                        :
                                    </Typography>
                                    <Typography>{errorId}</Typography>
                                </Stack>
                                <Stack
                                    direction="row"
                                    justifyContent="flex-end"
                                >
                                    <Button
                                        color="error"
                                        variant="outlined"
                                        size="small"
                                        onClick={setCopied}
                                    >
                                        {
                                            systemNotifications?.find(
                                                (x) =>
                                                    x.key ===
                                                    "label_settingPlNotificationBtn"
                                            )?.value
                                        }
                                    </Button>
                                </Stack>
                            </Stack>
                        </Collapse>
                    </Stack>
                </CardContent>
            </Card>
        </SnackbarContent>
    );
});

export default ServerError;
